import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getAppointmentsForDay, getAppointmentTypeClass, getHolidaysForDay } from '../../../utils/appointmentUtils';
import { 
  startOfWeek, 
  addDays, 
  addMonths, 
  isToday, 
  isSameDay 
} from '../../../utils/dateUtils';

const MiniMonthView = ({ 
  currentDate,
  appointments,
  holidays,
  onDayClick,
  isVisible,
  onClose,
  triggerRef
}) => {
  const miniMonthRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [viewDate, setViewDate] = useState(currentDate); // Track the month being viewed

  // Reset view date when mini month becomes visible
  useEffect(() => {
    if (isVisible) {
      setViewDate(currentDate);
    }
  }, [isVisible, currentDate]);

  // Calculate position based on trigger element
  useEffect(() => {
    if (isVisible && triggerRef.current && miniMonthRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const miniMonthRect = miniMonthRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = triggerRect.bottom + 8; // 8px gap below trigger
      let left = triggerRect.left;

      // Adjust if it would go off screen vertically
      if (top + miniMonthRect.height > viewportHeight) {
        top = triggerRect.top - miniMonthRect.height - 8; // Show above instead
      }

      // Adjust if it would go off screen horizontally
      if (left + miniMonthRect.width > viewportWidth) {
        left = viewportWidth - miniMonthRect.width - 16; // 16px margin from edge
      }

      setPosition({ top, left });
    }
  }, [isVisible, triggerRef]);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (miniMonthRef.current && !miniMonthRef.current.contains(event.target) && 
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose, triggerRef]);

  // Generate calendar days for the viewed month (not necessarily current month)
const generateCalendarDays = () => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  
  // First day of the month
  const firstDay = new Date(year, month, 1);
  
  // Start from the Sunday of the week containing the first day
  const startDate = startOfWeek(firstDay);
  
  // Generate 42 days (6 weeks) to fill the calendar grid
  const days = [];
  for (let i = 0; i < 42; i++) {
    days.push(addDays(startDate, i));
  }
  
  return days;
};

  // Navigation handlers
const handlePreviousMonth = (event) => {
  event.stopPropagation();
  setViewDate(addMonths(viewDate, -1));
};

const handleNextMonth = (event) => {
  event.stopPropagation();
  setViewDate(addMonths(viewDate, 1));
};

  const handleDayClick = (day, event) => {
    event.stopPropagation();
    onDayClick(day);
    onClose();
  };

  if (!isVisible) return null;

  const calendarDays = generateCalendarDays();
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="mini-month-backdrop" onClick={onClose} />

      {/* Mini Month Calendar */}
      <div
        ref={miniMonthRef}
        className="mini-month-view"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 10000
        }}
      >
        {/* Header with Navigation */}
        <div className="mini-month-header">
          <button 
            className="mini-month-nav-btn"
            onClick={handlePreviousMonth}
            title="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <h3 className="mini-month-title">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </h3>
          
          <button 
            className="mini-month-nav-btn"
            onClick={handleNextMonth}
            title="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Day headers */}
        <div className="mini-month-day-headers">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="mini-month-day-header">{day}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="mini-month-grid">
          {weeks.map((week, weekIndex) => (
            week.map((day, dayIndex) => {
              const dayAppointments = getAppointmentsForDay(appointments, day);
              const dayHolidays = getHolidaysForDay(holidays, day);
              const isTodayDate = isToday(day);
              const isCurrentMonth = day.getMonth() === viewDate.getMonth();
              const isSelectedDate = isSameDay(day, currentDate);
              
              return (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`mini-month-day ${isTodayDate ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${isSelectedDate ? 'selected' : ''} ${dayHolidays.length > 0 ? 'has-holiday' : ''}`}
                  onClick={(e) => handleDayClick(day, e)}
                >
                  <span className="mini-month-day-number">{day.getDate()}</span>
                  
                  {/* Holiday indicator */}
                  {dayHolidays.length > 0 && (
                    <span className="mini-month-holiday-dot">🕎</span>
                  )}
                  
                  {/* Appointment dots */}
                  {dayAppointments.length > 0 && (
                    <div className="mini-month-appointment-dots">
                      {dayAppointments.slice(0, 3).map((appointment, index) => (
                        <div
                          key={appointment.ID}
                          className={`mini-appointment-dot ${getAppointmentTypeClass(appointment.Title)}`}
                        />
                      ))}
                      {dayAppointments.length > 3 && (
                        <div className="mini-more-dots">•••</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

export default MiniMonthView;